import AutoResizeTextarea from '@/components/AutoResizeTextarea/AutoResizeTextarea';
import rem from '@/utils/rem';
import { Box, Editable, EditablePreview, EditableTextarea, Flex, ResponsiveValue, Text } from '@chakra-ui/react';
import React from 'react';
import { Control, Controller, FieldErrors, FieldValues, Path } from 'react-hook-form';

interface GlassCardProps {
  title: string;
  content: string;
  categoryColor: string;
  width?: ResponsiveValue<string>;
  maxWidth?: ResponsiveValue<string>;
  height?: ResponsiveValue<string>;
  alignSelf?: ResponsiveValue<'flex-start' | 'flex-end' | 'center'>;
  fitContent?: boolean;
}

const GlassCard: React.FC<GlassCardProps> = ({
  title,
  content,
  categoryColor,
  width = 'full',
  maxWidth,
  height,
  alignSelf = 'flex-end',
  fitContent = true,
}) => {
  return (
    <Flex
      flexDirection="column"
      alignSelf={alignSelf}
      overflow="hidden"
      width={width}
      maxWidth={maxWidth}
      height={height || (fitContent ? 'auto' : '100%')}
      minHeight="auto"
      maxHeight={rem(240)}
      padding={rem(20)}
      borderRadius={rem(4)}
      boxShadow="0 8px 32px rgba(0,0,0,0.1)"
      backdropFilter="blur(10px)"
      backgroundColor="rgba(255, 255, 255, 0.15)"
      data-testid="glass-card"
    >
      <Text
        flexShrink={0}
        display="inline-block"
        width="fit-content"
        marginBottom={rem(12)}
        padding={`${rem(2)} ${rem(6)}`}
        color="var(--Text-Primary, #111A26)"
        fontFamily="Satoshi"
        fontSize="9.429px"
        fontWeight="900"
        lineHeight="130%"
        letterSpacing="1px"
        fontStyle="normal"
        textTransform="uppercase"
        borderRadius={rem(3)}
        backgroundColor={categoryColor}
      >
        {title}
      </Text>

      {fitContent ? (
        <Box>
          <Text
            color="#FFF"
            fontFamily="Satoshi"
            fontSize="17px"
            fontWeight="500"
            lineHeight="110%"
            letterSpacing="-0.34px"
            fontStyle="normal"
          >
            {content}
          </Text>
        </Box>
      ) : (
        <Box
          sx={{
            '&::-webkit-scrollbar': {
              width: '4px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '2px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '2px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(255, 255, 255, 0.5)',
            },
          }}
          flex="1"
          overflowY="auto"
          paddingRight={rem(4)}
        >
          <Text
            color="#FFF"
            fontFamily="Satoshi"
            fontSize="17px"
            fontWeight="500"
            letterSpacing="-0.34px"
            fontStyle="normal"
          >
            {content}
          </Text>
        </Box>
      )}
    </Flex>
  );
};

interface EditableGlassCardProps<T extends FieldValues = FieldValues> {
  title: string;
  control: Control<T>;
  name: Path<T>;
  placeholder: string;
  categoryColor: string;
  width?: ResponsiveValue<string>;
  maxWidth?: ResponsiveValue<string>;
  height?: ResponsiveValue<string>;
  alignSelf?: ResponsiveValue<'flex-start' | 'flex-end' | 'center'>;
  fitContent?: boolean;
  isSubmitting?: boolean;
  errors?: FieldErrors<T>;
  onBlur?: (prevValue: string, value: string | undefined) => void;
  onSubmit?: (value: string | undefined) => void;
}

const EditableGlassCard = <T extends FieldValues = FieldValues>({
  title,
  control,
  name,
  placeholder,
  categoryColor,
  width = 'full',
  maxWidth,
  height,
  alignSelf = 'flex-end',
  fitContent = true,
  isSubmitting = false,
  errors,
  onBlur,
  onSubmit,
}: EditableGlassCardProps<T>) => {
  return (
    <Flex
      flexDirection="column"
      alignSelf={alignSelf}
      overflow="hidden"
      width={width}
      maxWidth={maxWidth}
      height={height || (fitContent ? 'auto' : '100%')}
      padding={rem(20)}
      borderRadius={rem(4)}
      boxShadow="0 8px 32px rgba(0,0,0,0.1)"
      backdropFilter="blur(10px)"
      backgroundColor="rgba(255, 255, 255, 0.15)"
      data-testid="editable-glass-card"
    >
      <Text
        flexShrink={0}
        display="inline-block"
        width="fit-content"
        marginBottom={rem(12)}
        padding={`${rem(2)} ${rem(6)}`}
        color="var(--Text-Primary, #111A26)"
        fontFamily="Satoshi"
        fontSize="9.429px"
        fontWeight="900"
        lineHeight="130%"
        letterSpacing="1px"
        fontStyle="normal"
        textTransform="uppercase"
        borderRadius={rem(3)}
        backgroundColor={categoryColor}
      >
        {title}
      </Text>

      <Controller
        name={name}
        control={control}
        render={({ field: { ref, value, onChange } }) => (
          <>
            <Editable
              ref={ref}
              overflow="hidden"
              width="full"
              minWidth={0}
              height="full"
              color="text-primary"
              fontFamily="Satoshi"
              fontSize="17px"
              fontWeight="500"
              lineHeight="1.5"
              defaultValue={value ?? ''}
              isDisabled={isSubmitting}
              onBlur={(prevValue) => onBlur?.(prevValue, value)}
              onChange={onChange}
              onSubmit={() => onSubmit?.(value)}
              placeholder={placeholder}
              selectAllOnFocus={false}
              submitOnBlur
              value={value ?? ''}
            >
              <EditablePreview
                width="full"
                height="full"
                color="text-primary"
                fontStyle={value ? 'normal' : 'italic'}
                opacity={value ? 1 : 0.7}
                whiteSpace="pre-wrap"
                wordBreak="break-word"
                overflowWrap="break-word"
              />
              <EditableTextarea
                as={AutoResizeTextarea}
                color="text-primary"
                _focusVisible={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  boxShadow: 'none',
                  border: 'none',
                  lineHeight: 'inherit',
                }}
                _placeholder={{
                  fontStyle: 'italic',
                }}
                whiteSpace="pre-wrap"
                wordBreak="break-word"
                overflowWrap="break-word"
              />
            </Editable>

            {errors?.[name] && typeof errors[name]?.message === 'string' && (
              <Text as="span" marginTop={rem(4)} color="red.300" fontSize="small">
                {errors[name]?.message as string}
              </Text>
            )}
          </>
        )}
      />
    </Flex>
  );
};

export { EditableGlassCard };
export default GlassCard;
